import React from 'react';
import { Link } from 'react-router-dom';
import { 
  MessageSquare, Users, Zap, Calendar, Waves,
  Hash, ThumbsUp, Share2, PlusCircle, MoreHorizontal, Trophy, Activity 
} from 'lucide-react';
import { BRAND_GREEN, BRAND_GOLD } from '../theme/sucronTheme';
import { useSportsLang } from '../contexts/SportsLangContext';

const LABELS = {
  pt: {
    communityTitle: 'Comunidade Natação',
    shareBtn: 'Compartilhar Treino/Tempo',
    tabs: ['Geral', 'Competições', 'Treinos', 'Águas Abertas', 'Master', 'Técnica'],
    postPlaceholder: 'Como foi o treino hoje? Compartilhe seus tempos...',
    calendarTitle: 'Calendário',
    viewAll: 'Ver todos',
    highlightsTitle: 'Destaques da Semana',
    topClubs: 'Clubes em Alta',
    share: 'Compartilhar',
  },
  en: {
    communityTitle: 'Swimming Community',
    shareBtn: 'Share Training/Time',
    tabs: ['General', 'Competitions', 'Training', 'Open Water', 'Masters', 'Technique'],
    postPlaceholder: 'How was training today? Share your times...',
    calendarTitle: 'Calendar',
    viewAll: 'See all',
    highlightsTitle: 'Weekly Highlights',
    topClubs: 'Top Clubs',
    share: 'Share',
  }
};

// === CORES & TEMA ===
const THEME = {
  primary: BRAND_GREEN,
  secondary: '#00D9A3',
  gold: BRAND_GOLD,
  bg: '#f3f2ef',
  white: '#ffffff',
  text: '#1f2937',
  border: '#e5e7eb'
};

// === COMPONENTE 1: HEADER DA COMUNIDADE ===
const CommunityHeader = () => {
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  return (
  <div className="bg-white border-b border-gray-200 sticky top-0 z-20 shadow-sm">
    <div className="max-w-7xl mx-auto px-4 py-4">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center border border-blue-100">
            <Waves size={24} className="text-blue-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 leading-tight">{t.communityTitle}</h1>
            <p className="text-xs text-gray-500 font-medium flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></span>
              850 online • 32 novos tópicos hoje
            </p>
          </div>
        </div>
        
        {/* Botão de Ação Principal */}
        <button className="hidden md:flex items-center gap-2 px-4 py-2 text-white rounded-lg font-bold text-sm hover:opacity-90 transition-colors shadow-sm" style={{ backgroundColor: BRAND_GREEN }}>
          <PlusCircle size={18} />
          {t.shareBtn}
        </button>
      </div>

      {/* Navegação de Canais (Abas) */}
      <div className="flex gap-1 mt-6 overflow-x-auto pb-2 scrollbar-hide">
        {t.tabs.map((tab, i) => (
          <button 
            key={tab}
            className={`px-4 py-2 rounded-full text-sm font-bold whitespace-nowrap transition-all ${
              i === 0 
                ? 'bg-blue-50 text-blue-700 border border-blue-200' 
                : 'text-gray-600 hover:bg-gray-50 border border-transparent'
            }`}
          >
            {i === 0 && <Hash size={14} className="inline mr-1 -mt-0.5" />}
            {tab}
          </button>
        ))}
      </div>
    </div>
  </div>
  );
};

// === COMPONENTE 2: POST DO FEED (Adaptado para Natação) ===
const CommunityPost = ({ author, time, content, likes, comments, tag, image, type }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer mb-4">
    {/* Cabeçalho do Post */}
    <div className="flex justify-between items-start mb-3">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-gray-200 overflow-hidden border border-gray-100">
          <img src={`https://ui-avatars.com/api/?name=${author}&background=random`} alt={author} />
        </div>
        <div>
          <h4 className="font-bold text-sm text-gray-900">{author}</h4>
          <p className="text-xs text-gray-500">{time} • <span className="text-blue-600 font-medium">{tag}</span></p>
        </div>
      </div>
      <button className="text-gray-400 hover:text-gray-600">
        <MoreHorizontal size={18} />
      </button>
    </div>

    {/* Conteúdo */}
    <p className="text-gray-800 text-sm leading-relaxed mb-3 whitespace-pre-line">
      {content}
    </p>

    {/* Widget de Resultado (Se for post de tempo) */}
    {type === 'result' && (
      <div className="mb-3 bg-gray-50 rounded-lg p-3 border border-gray-200 flex items-center justify-between">
        <div>
          <p className="text-xs font-bold text-gray-500 uppercase">50m Livre</p>
          <p className="text-lg font-bold" style={{ color: BRAND_GREEN }}>24.50s</p>
        </div>
        <div className="text-right">
          <span className="text-xs font-bold px-2 py-1 rounded" style={{ backgroundColor: '#d1fae5', color: BRAND_GREEN }}>PB (Personal Best)</span>
        </div>
      </div>
    )}

    {/* Imagem (Opcional) */}
    {image && (
      <div className="mb-3 rounded-lg overflow-hidden border border-gray-100">
        <img src={image} alt="Conteúdo" className="w-full h-48 object-cover" />
      </div>
    )}

    {/* Rodapé de Ações */}
    <div className="flex items-center gap-6 pt-3 border-t border-gray-50">
      <button className="flex items-center gap-1.5 text-xs font-bold text-gray-500 hover:text-blue-600 transition-colors">
        <ThumbsUp size={16} /> {likes}
      </button>
      <button className="flex items-center gap-1.5 text-xs font-bold text-gray-500 hover:text-blue-600 transition-colors">
        <MessageSquare size={16} /> {comments}
      </button>
      <button className="flex items-center gap-1.5 text-xs font-bold text-gray-500 hover:text-blue-600 transition-colors ml-auto">
        <Share2 size={16} /> Compartilhar
      </button>
    </div>
  </div>
);

// === COMPONENTE 3: WIDGET LATERAL (COMPETIÇÕES) ===
const UpcomingCompetitionsWidget = () => {
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  return (
  <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-6">
    <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
      <h3 className="font-bold text-sm text-gray-900 flex items-center gap-2">
        <Calendar size={16} style={{ color: BRAND_GOLD }} />
        {t.calendarTitle}
      </h3>
      <Link to="/agenda" className="text-xs font-bold text-blue-600 hover:underline">{t.viewAll}</Link>
    </div>
    <div className="divide-y divide-gray-100">
       {[
         { name: 'Troféu José Finkel', date: '12 Ago', loc: 'Recife' },
         { name: 'Mundial Junior', date: '04 Set', loc: 'Israel' },
         { name: 'Regional Sul', date: '15 Set', loc: 'Floripa' }
       ].map((comp, i) => (
         <div key={i} className="p-3 hover:bg-gray-50 transition-colors cursor-pointer">
            <div className="flex justify-between items-start">
               <span className="font-bold text-sm text-gray-800">{comp.name}</span>
               <span className="text-xs font-mono font-bold px-1.5 py-0.5 rounded" style={{ color: BRAND_GREEN, backgroundColor: '#d1fae5' }}>{comp.date}</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">{comp.loc}</p>
         </div>
       ))}
    </div>
  </div>
  );
};

// === COMPONENTE 4: WIDGET LATERAL (RANKINGS) ===
const TopSwimmersWidget = () => {
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  return (
  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
    <h3 className="font-bold text-sm text-gray-900 mb-4 flex items-center gap-2">
      <Zap size={16} className="text-yellow-500" />
      {t.highlightsTitle}
    </h3>
    <div className="space-y-4">
      {[
        { name: 'Guilherme Costa', event: '400m Livre', time: '3:43.31' },
        { name: 'Beatriz Dizotti', event: '1500m Livre', time: '16:05.25' },
        { name: 'Kayky Mota', event: '100m Borboleta', time: '51.47' }
      ].map((swimmer, i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center font-bold text-xs border border-gray-200">
            {i + 1}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-gray-800 truncate">{swimmer.name}</p>
            <p className="text-xs text-gray-500 truncate">{swimmer.event} • {swimmer.time}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
  );
};

// === PÁGINA PRINCIPAL ===
export default function SwimmingCommunityPage() {
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  return (
    <div className="min-h-screen pb-12 font-sans" style={{ backgroundColor: THEME.bg }}>
      
      {/* 1. Header Fixo */}
      <CommunityHeader />

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          
          {/* 2. COLUNA PRINCIPAL (FEED) - 8 colunas */}
          <div className="lg:col-span-8 space-y-4">
            
            {/* Caixa de Criação Rápida */}
            <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm flex gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold">EU</div>
              <input 
                type="text" 
                placeholder={t.postPlaceholder} 
                className="flex-1 bg-gray-50 rounded-full px-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all hover:bg-gray-100"
              />
            </div>

            {/* Posts */}
            <CommunityPost 
              author="Lucas Santos"
              time="30 min atrás"
              tag="Treino"
              content="Hoje o foco foi série de A2. 10x 100m saindo a 1:30. Consegui manter média de 1:08. Sentindo a evolução na resistência! ‍♂️"
              likes={24}
              comments={5}
            />

            <CommunityPost 
              author="Mariana Costa"
              time="2h atrás"
              tag="Resultados"
              content="Muito feliz com o resultado no Regional! Baixei meu tempo nos 50m livre. O trabalho de saída e virada fez toda a diferença."
              type="result"
              likes={156}
              comments={32}
            />

            <CommunityPost 
              author="Coach Roberto"
              time="4h atrás"
              tag="Técnica"
              content="Dica rápida sobre a fase submersa no nado costas: Mantenha o corpo alinhado e evite 'sentar' na água. O quadril alto reduz o arrasto significativamente. Vejam esse exemplo:"
              image="https://swimswam.com/wp-content/uploads/2019/04/Backstroke-Start.jpg"
              likes={89}
              comments={12}
            />

          </div>

          {/* 3. COLUNA LATERAL (WIDGETS) - 4 colunas */}
          <div className="lg:col-span-4 space-y-6">
            
            {/* Widget de Competições */}
            <UpcomingCompetitionsWidget />

            {/* Widget de Top Nadadores */}
            <TopSwimmersWidget />

            {/* Widget de Clubes */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
              <h3 className="font-bold text-sm text-gray-900 mb-4 flex items-center gap-2">
                <Trophy size={16} style={{ color: BRAND_GREEN }} />
                {t.topClubs}
              </h3>
              <div className="flex flex-wrap gap-2">
                {['Minas Tênis', 'Pinheiros', 'Corinthians', 'Flamengo', 'Unisanta'].map(club => (
                  <span key={club} className="text-xs font-semibold px-2 py-1 bg-gray-50 border border-gray-200 rounded-md text-gray-600">
                    {club}
                  </span>
                ))}
              </div>
            </div>

          </div>

        </div>
      </div>
    </div>
  );
}
